export interface UserProfile {
  id: string;
  username: string;
  displayName: string;
  email: string;
  avatar?: string;
  bio: string;
  age: number;
  gender: Gender;
  characterClass: CharacterClass;
  lookingFor: Gender[];
  interests: string[];
  location?: string;
  preferences: UserPreferences;
  createdAt: Date;
  lastActive: Date;
  isOnline: boolean;
  /**
   * True when the profile belongs to an under-18 user. Anonymous mode is
   * disabled for minors at both the UI and server (Phase B, AC-08.1/2).
   * Backfilled from `age < 18` in migration 20260501_002.
   */
  isMinor?: boolean;
}

/**
 * Gender type - updated to be more inclusive
 * NOTE: 'man' and 'woman' are the primary values used in new code.
 * 'male' and 'female' are kept for backward compatibility with existing data.
 */
export type Gender = 'man' | 'woman' | 'non-binary' | 'other' | 'male' | 'female';

/** Legacy gender type alias for backward compatibility */
export type LegacyGender = 'male' | 'female';

export const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'man', label: 'Man' },
  { value: 'woman', label: 'Woman' },
  { value: 'non-binary', label: 'Non-binary' },
  { value: 'other', label: 'Other' },
];

/** Options for "interested in" selections */
export const INTERESTED_IN_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'man', label: 'Men' },
  { value: 'woman', label: 'Women' },
  { value: 'non-binary', label: 'Non-binary people' },
  { value: 'other', label: 'Everyone' },
];

/**
 * Convert gender to character model suffix for Unity
 * Non-binary and other default to 'Boy' (can be customized separately)
 */
export function genderToCharacterSuffix(gender: Gender): 'Boy' | 'Girl' {
  return gender === 'woman' || gender === 'female' ? 'Girl' : 'Boy';
}

/**
 * Normalize legacy gender values to new format
 */
export function normalizeGender(gender: Gender): 'man' | 'woman' | 'non-binary' | 'other' {
  if (gender === 'male') return 'man';
  if (gender === 'female') return 'woman';
  return gender;
}

export type CharacterClass = 'tank' | 'archer' | 'mage' | 'rogue';

export const CHARACTER_CLASSES: { value: CharacterClass; label: string; description: string }[] = [
  { value: 'tank', label: 'Tank', description: 'Strong and resilient warrior' },
  { value: 'archer', label: 'Archer', description: 'Swift and precise ranged fighter' },
  { value: 'mage', label: 'Mage', description: 'Powerful magic wielder' },
  { value: 'rogue', label: 'Rogue', description: 'Stealthy and cunning assassin' },
];

export interface UserPreferences {
  minAge: number;
  maxAge: number;
  maxDistance?: number;
  interests: string[];
  showOnlineOnly: boolean;
}

export interface Room {
  id: string;
  code: string;
  name: string;
  description: string;
  context: RoomContext;
  hostId: string;
  hostName: string;
  minAge: number;
  maxAge: number;
  maxPlayers: number;
  currentPlayers: number;
  players: RoomPlayer[];
  tags: string[];
  isPrivate: boolean;
  hasPassword: boolean;
  requiresApproval: boolean;
  password?: string;
  createdAt: Date;
  status: RoomStatus;
  /** Optional authored scene template id (e.g. "Flexo Commons"); server default when absent. */
  sceneTemplateId?: string;
  /** P4 catalog taxonomy (denormalized from the template; defaults to 'lobbies'). */
  category?: RoomCategory;
  subCategory?: string;
}

export type RoomContext =
  | 'casual-hangout'
  | 'speed-dating'
  | 'group-activity'
  | 'adventure-quest'
  | 'coffee-chat'
  | 'game-night';

export type RoomStatus = 'waiting' | 'in-progress' | 'full' | 'closed';

/**
 * P4 room-catalog taxonomy. Replaces the old 'all'|'dating'|'gaming' triad.
 * The concrete browse buckets are games / lobbies / interests / dating; 'all'
 * is the no-filter tab.
 */
export type RoomCategory = 'all' | 'games' | 'lobbies' | 'interests' | 'dating';

/** The four concrete catalog buckets (excludes the 'all' tab). */
export type RoomBucket = Exclude<RoomCategory, 'all'>;

/** Map room contexts to a category (fallback when the server sends no category). */
export const ROOM_CONTEXT_CATEGORIES: Record<RoomContext, RoomBucket> = {
  'speed-dating': 'dating',
  'coffee-chat': 'dating',
  'casual-hangout': 'lobbies',
  'adventure-quest': 'games',
  'game-night': 'games',
  'group-activity': 'games',
};

/** Room category tabs for the dashboard. */
export const ROOM_CATEGORY_TABS: { value: RoomCategory; label: string; icon: string; description: string }[] = [
  { value: 'all', label: 'All', icon: '🏠', description: 'Browse everything' },
  { value: 'games', label: 'Games', icon: '🎮', description: 'Objective-driven co-op & competitive worlds' },
  { value: 'lobbies', label: 'Lobbies', icon: '🛋️', description: 'Hangout & social spaces' },
  { value: 'interests', label: 'Interests', icon: '🎯', description: 'Topic-themed hangouts' },
  { value: 'dating', label: 'Dating', icon: '💘', description: 'Speed dating & date-night formats' },
];

const VALID_BUCKETS: RoomBucket[] = ['games', 'lobbies', 'interests', 'dating'];

/**
 * Coerce a stored/legacy category value into a valid bucket. Maps the retired
 * 'gaming' → 'games' and anything unknown → 'lobbies' (the safe default).
 */
export function normalizeRoomCategory(value?: string | null): RoomBucket {
  if (value === 'gaming') return 'games';
  return (value && (VALID_BUCKETS as string[]).includes(value)) ? (value as RoomBucket) : 'lobbies';
}

/**
 * The category a room browses under. Prefers the server-denormalized category,
 * falling back to the room-context mapping for rooms created before P4.
 */
export function getRoomCategory(room: Pick<Room, 'category' | 'context'>): RoomBucket {
  if (room.category) return normalizeRoomCategory(room.category);
  return ROOM_CONTEXT_CATEGORIES[room.context] ?? 'lobbies';
}

export type ChatRole = 'system' | 'dungeon_master' | 'user' | 'spectator';

export interface RoomPlayer {
  id: string;
  displayName: string;
  avatar?: string;
  characterType?: string;
  gender?: Gender;
  /** Synty character appearance data as JSON string */
  appearanceData?: string;
  isHost: boolean;
  joinedAt: Date;
  // Chat role fields
  chatRole?: ChatRole;
  chatAvailable?: boolean;
}

export interface RoomFilters {
  search: string;
  context: RoomContext | 'all';
  minAge: number;
  maxAge: number;
  showFull: boolean;
  showPrivate: boolean;
}

export const ROOM_CONTEXTS: { value: RoomContext; label: string; description: string }[] = [
  { value: 'casual-hangout', label: 'Casual Hangout', description: 'Relaxed atmosphere to meet new people' },
  { value: 'speed-dating', label: 'Speed Dating', description: 'Quick rounds to find your match' },
  { value: 'group-activity', label: 'Group Activity', description: 'Team-based fun and games' },
  { value: 'adventure-quest', label: 'Adventure Quest', description: 'Embark on an adventure together' },
  { value: 'coffee-chat', label: 'Coffee Chat', description: 'Intimate conversations over virtual coffee' },
  { value: 'game-night', label: 'Game Night', description: 'Competitive fun with potential matches' },
];

export const INTERESTS = [
  'Gaming', 'Music', 'Movies', 'Travel', 'Cooking', 'Fitness',
  'Reading', 'Art', 'Photography', 'Dancing', 'Sports', 'Technology',
  'Nature', 'Pets', 'Fashion', 'Food', 'Comedy', 'Adventure'
];
